// LearnModal.jsx
import React, { useEffect, useRef, useState } from "react";
import { X, ChevronDown, ChevronUp } from "lucide-react";
import { useTranslation } from "../../i18n";

const toHtml = (value) => ({ __html: value });
const classNames = (...values) => values.filter(Boolean).join(" ");

const DEFAULT_STRINGS = {
  title: "How does ThousandYearKingdom.org work?",
  ariaLabel: "How does ThousandYearKingdom.org work?",
  closeLabel: "Close",
};

const DEFAULT_SECTIONS = [
  {
    title: "Mission",
    blocks: [
      {
        type: "paragraph",
        className: "mb-3",
        text: "The purpose of this platform is twofold:",
      },
      {
        type: "orderedList",
        className: "mb-3",
        items: [
          "<strong>To increase the Kingdom</strong> — helping people everywhere encounter Jesus and live as disciples in every sphere of life.",
          "<strong>To unite the Body of Christ across the world</strong> — breaking down denominational and geographic barriers so we can work together as one.",
        ],
      },
      {
        type: "paragraph",
        text: "Our shared vision is to help usher in the <strong>Kingdom Age on earth</strong> by creating practical tools that let believers collaborate, decide, and act together in a way that is fair, transparent, and scalable.",
      },
    ],
  },
  {
    title: "Communities United Around a Shared Mission",
    blocks: [
      {
        type: "paragraph",
        text: "At the core of the platform is the <strong>Community</strong> — people gathered around a shared Kingdom mission. Communities could be local churches, global ministries, businesses, farms, or service teams. Every member can contribute ideas, participate in discernment, and help the community move into action together.",
      },
    ],
  },
  {
    title: "Ideation & Voting",
    blocks: [
      {
        type: "paragraph",
        className: "mb-3",
        text: "New ideas are introduced as <strong>Campaigns</strong>. Anyone in a Community can propose an idea, and the community decides together through transparent voting.",
      },
      {
        type: "paragraph",
        className: "mb-3",
        text: "The process gives groups a practical way to coordinate without becoming chaotic or disorganized:",
      },
      {
        type: "list",
        items: [
          "Ideas move through clear stages: proposal, refinement, approval, and execution.",
          "Like Agile, campaigns iterate in short cycles to adapt and improve.",
          "Every eligible member can be heard and participate in transparent decisions.",
        ],
      },
    ],
  },
  {
    title: "Transparency & Accountability",
    blocks: [
      {
        type: "list",
        items: [
          "<strong>Honesty</strong> in decision-making — every vote is recorded.",
          "<strong>Accountability</strong> — every policy and budget is visible.",
          "<strong>Clarity</strong> — every transaction is tracked.",
        ],
      },
    ],
  },
  {
    title: "Dynamic Policies",
    blocks: [
      {
        type: "paragraph",
        text: "Policies are <strong>living agreements</strong>, continuously open to refinement and adjustment. Every user has a say on every single policy, and policies can evolve as circumstances change. The community maintains control at all times.",
      },
    ],
  },
  {
    title: "Marketplace",
    blocks: [
      {
        type: "list",
        items: [
          "Trade <strong>Goods and Services</strong> with trusted members.",
          "Use <strong>Barter</strong> for peer-to-peer exchange.",
          "Use the payment and stewardship methods that each community has enabled.",
        ],
      },
    ],
  },
  {
    title: "The Bigger Picture",
    blocks: [
      {
        type: "paragraph",
        text: "This is a <strong>new way of organizing community life</strong> — rooted in faith, powered by transparency, and governed by everyone, not just a few. It scales from a small group of friends to a global body united in purpose.",
      },
    ],
  },
];

const sanitizeSections = (rawSections) => {
  const sections = Array.isArray(rawSections) && rawSections.length
    ? rawSections
    : DEFAULT_SECTIONS;

  return sections.map((section, index) => {
    const fallback = DEFAULT_SECTIONS[index] || {};
    const title =
      typeof section?.title === "string" && section.title.trim().length
        ? section.title
        : fallback.title;
    const blocks = Array.isArray(section?.blocks) && section.blocks.length
      ? section.blocks
      : fallback.blocks || [];

    const normalizedBlocks = blocks
      .map((block, blockIndex) => {
        if (!block || typeof block !== "object") {
          return fallback.blocks?.[blockIndex] || null;
        }
        const fallbackBlock = fallback.blocks?.[blockIndex] || {};
        return {
          type: block.type || fallbackBlock.type,
          text: typeof block.text === "string" ? block.text : fallbackBlock.text,
          items: Array.isArray(block.items) && block.items.length
            ? block.items
            : fallbackBlock.items || [],
          className: block.className || fallbackBlock.className,
        };
      })
      .filter(Boolean);

    return { title, blocks: normalizedBlocks };
  });
};

const renderBlock = (block, blockIndex) => {
  if (!block || typeof block !== "object") return null;
  const keyBase = `${block.type || "block"}-${blockIndex}`;
  if (block.type === "paragraph") {
    return (
      <p
        key={keyBase}
        className={block.className}
        dangerouslySetInnerHTML={toHtml(block.text || "")}
      />
    );
  }
  if (block.type === "orderedList") {
    const listClass = classNames(
      "list-decimal pl-5 space-y-1",
      block.className
    );
    return (
      <ol key={keyBase} className={listClass}>
        {(block.items || []).map((item, idx) => (
          <li key={`${keyBase}-${idx}`} dangerouslySetInnerHTML={toHtml(item)} />
        ))}
      </ol>
    );
  }
  if (block.type === "list") {
    const listClass = classNames("list-disc pl-5 space-y-2", block.className);
    return (
      <ul key={keyBase} className={listClass}>
        {(block.items || []).map((item, idx) => (
          <li key={`${keyBase}-${idx}`} dangerouslySetInnerHTML={toHtml(item)} />
        ))}
      </ul>
    );
  }
  return null;
};

export default function LearnModal({ onClose }) {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState(0);
  const modalRef = useRef(null);

  const getString = (key, fallback) => {
    const value = t(key);
    return typeof value === "string" && value !== key ? value : fallback;
  };

  const translatedSections = sanitizeSections(t("learnModal.sections"));
  const title = getString("learnModal.title", DEFAULT_STRINGS.title);
  const ariaLabel = getString("learnModal.ariaLabel", DEFAULT_STRINGS.ariaLabel);
  const closeLabel = getString("learnModal.closeLabel", DEFAULT_STRINGS.closeLabel);

  // Close on ESC
  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [onClose]);

  // Basic focus trap
  useEffect(() => {
    const root = modalRef.current;
    if (!root) return;
    const getFocusables = () =>
      Array.from(
        root.querySelectorAll(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
        )
      );
    const onKeyDown = (e) => {
      if (e.key !== "Tab") return;
      const f = getFocusables();
      if (f.length === 0) return;
      const first = f[0],
        last = f[f.length - 1];
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || !root.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last || !root.contains(active)) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    root.addEventListener("keydown", onKeyDown);
    return () => root.removeEventListener("keydown", onKeyDown);
  }, []);

  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/70 backdrop-blur">
      <div
        ref={modalRef}
        className="relative w-full max-w-[min(92vw,40rem)] sm:max-w-3xl lg:max-w-4xl max-h-[85vh] overflow-y-auto bg-secondary/95 text-white rounded-2xl shadow-2xl border border-white/10"
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-5 py-4 border-b border-white/10 sticky top-0 bg-secondary/95 rounded-t-2xl z-10">
          <h2 className="text-xl font-bold">{title}</h2>
          <button
            onClick={onClose}
            className="p-2 rounded hover:bg-white/10 focus:outline-none"
            aria-label={closeLabel}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Accordion */}
        <div className="divide-y divide-white/10">
          {translatedSections.map((sec, i) => {
            const isOpen = i === openIndex;
            return (
              <div key={`${sec.title}-${i}`}>
                <button
                  onClick={() => toggle(i)}
                  className="w-full flex justify-between items-center px-4 sm:px-6 py-3 sm:py-4 text-left hover:bg-white/5 focus:outline-none"
                  aria-expanded={isOpen}
                >
                  <span className="font-semibold">{sec.title}</span>
                  {isOpen ? <ChevronUp /> : <ChevronDown />}
                </button>
                {isOpen && (
                  <div className="px-4 sm:px-6 pb-5 text-sm sm:text-base text-white/90 space-y-3 animate-fadeIn">
                    {sec.blocks.map((block, blockIndex) =>
                      renderBlock(block, blockIndex)
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
